import type {FC} from 'react';
import {useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import type {FrontendNzbsu, RecentNzbsuData} from '@repo/shared/api/website_api';
import {NO_IMDB_ATTRIBUTE} from '@repo/shared/constant';
import {getNzbCheckHealth} from '@repo/shared/lib/nzb_check_stats';
import {errorAsString} from '@repo/shared/lib/type_utils';

import {Button, UnthemedNavLink} from '@repo/shared-web/components/core/button';

import {apiCall} from '@repo/website-frontend/lib/api';
import {formatBytes} from '@repo/website-frontend/lib/bytes';
import {formatDate} from '@repo/website-frontend/lib/date';

const PAGE_SIZE = 50;

function mergeReleases(primary: FrontendNzbsu[], secondary: FrontendNzbsu[]): FrontendNzbsu[] {
  const byGuid = new Map<string, FrontendNzbsu>();
  for (const item of [...primary, ...secondary]) {
    if (!byGuid.has(item.guid)) {
      byGuid.set(item.guid, item);
    }
  }
  return [...byGuid.values()].sort((left, right) => right.pubTs - left.pubTs);
}

export const RecentNzbsuPage: FC = () => {
  const [releases, setReleases] = useState<RecentNzbsuData>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    apiCall('POST /list-recent-nzbsu', {limit: PAGE_SIZE, paginationToken: undefined})
      .then(result => {
        if (active) {
          setReleases(current => ({
            items: current ? mergeReleases(result.items, current.items) : result.items,
            paginationToken: result.paginationToken,
          }));
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const handleRefreshClick = useCallback(() => {
    setRefreshKey(current => current + 1);
  }, []);

  const handleLoadMoreClick = useCallback(async () => {
    if (!releases?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /list-recent-nzbsu', {
        limit: PAGE_SIZE,
        paginationToken: releases.paginationToken,
      });
      setReleases(current =>
        current
          ? {
              items: mergeReleases(current.items, nextPage.items),
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      setError(errorAsString(err));
    } finally {
      setLoadingMore(false);
    }
  }, [releases?.paginationToken]);

  return (
    <Wrapper>
      <Header>
        <h2>Most recent NZBSU releases</h2>
        <Actions>
          {releases ? <Count>{releases.items.length.toLocaleString()} shown</Count> : null}
          <Button onClick={handleRefreshClick} disabled={loading}>
            Refresh
          </Button>
        </Actions>
      </Header>
      {error ? <ErrorMessage role="alert">{error}</ErrorMessage> : null}
      {loading && !releases ? (
        <Message>Loading recent NZBSU releases…</Message>
      ) : releases?.items.length ? (
        <TableScroll>
          <ReleasesTable>
            <caption>NZBSU releases ordered by publication time, newest first</caption>
            <thead>
              <tr>
                <th scope="col">Posted</th>
                <th scope="col">Release</th>
                <th scope="col">Size</th>
                <th scope="col">Grabs</th>
                <th scope="col">IMDb</th>
                <th scope="col">Check</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              {releases.items.map(item => {
                const health = getNzbCheckHealth(item.lastCheck);
                return (
                  <tr key={item.guid}>
                    <td title={new Date(item.pubTs).toLocaleString()}>
                      {formatDate(new Date(item.pubTs))}
                    </td>
                    <td>
                      <ReleaseTitle title={item.title}>{item.title}</ReleaseTitle>
                      <ReleaseGuid>{item.guid}</ReleaseGuid>
                    </td>
                    <td>{formatBytes(item.size, 1)}</td>
                    <td>{item.grabs?.toLocaleString() ?? '—'}</td>
                    <td>
                      {item.imdb === NO_IMDB_ATTRIBUTE ? (
                        '—'
                      ) : (
                        <MovieLink to={`/browse/${item.imdb}`}>{item.imdb}</MovieLink>
                      )}
                    </td>
                    <HealthCell $health={health}>
                      {item.lastCheck ? (
                        <span title={`Checked ${new Date(item.lastCheck.ts).toLocaleString()}`}>
                          {health === 'healthy'
                            ? 'Healthy'
                            : health === 'unhealthy'
                              ? 'Unhealthy'
                              : 'Unknown'}
                        </span>
                      ) : (
                        'Not checked'
                      )}
                    </HealthCell>
                    <td>
                      <DetailsLink
                        to={`/admin?tab=find&guid=${encodeURIComponent(item.guid)}`}
                        aria-label={`View check details for ${item.title}`}
                      >
                        Details
                      </DetailsLink>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ReleasesTable>
        </TableScroll>
      ) : (
        <Message>No NZBSU releases are available.</Message>
      )}
      {releases?.paginationToken ? (
        <Button onClickAsync={handleLoadMoreClick} loading={loadingMore}>
          Load older releases
        </Button>
      ) : null}
    </Wrapper>
  );
};
RecentNzbsuPage.displayName = 'RecentNzbsuPage';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  h2 {
    margin: 0;
    font-size: 20px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Count = styled.span`
  color: #ffffff88;
  font-size: 13px;
`;

const ErrorMessage = styled.p`
  margin: 0;
  color: #ff8585;
`;

const Message = styled.p`
  margin: 0;
  padding: 16px;
  color: #ffffff88;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  border-radius: 6px;
  background: #1f242f;
  border: 1px solid #ffffff1a;
  padding: 14px 16px;
`;

const ReleasesTable = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  width: max-content;
  text-align: left;
  th {
    color: #ffffff88;
    font-size: 13px;
    font-weight: 500;
  }
  th,
  td {
    border-bottom: 1px solid #ffffff12;
    padding: 10px;
    white-space: nowrap;
    vertical-align: middle;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
  }
  tr:last-child td {
    border-bottom: 0;
  }
`;

const ReleaseTitle = styled.strong`
  display: block;
  max-width: 440px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReleaseGuid = styled.span`
  display: block;
  margin-top: 2px;
  color: #ffffff77;
  font: 11px monospace;
`;

const MovieLink = styled(UnthemedNavLink)`
  color: #a9d2ff;
`;

const DetailsLink = styled(UnthemedNavLink)`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  color: #a9d2ff;
  background: #ffffff10;
  &:hover {
    background: #ffffff18;
  }
`;

const HealthCell = styled.td<{$health: 'healthy' | 'unhealthy' | 'unknown'}>`
  color: ${p =>
    p.$health === 'healthy' ? '#9ee6ae' : p.$health === 'unhealthy' ? '#ff9c96' : '#ffffff88'};
`;
